import { Component, effect, ElementRef, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { PrimeNGConfig } from 'primeng/api';
import { Router } from '@angular/router';
import { TranslateService } from '@devinforius/i-essentials';
import { SpellcheckerService } from '@devinforius/i-dx-editor';
import { SsoService } from '@devinforius/i-sso';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  public loading = false;

  private readonly initialUrl = window.location.pathname + window.location.search;

  private readonly primeNGConfig = inject(PrimeNGConfig);
  private readonly spellcheckerService = inject(SpellcheckerService);
  private readonly router = inject(Router);
  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly translateService = inject(TranslateService);
  private readonly ssoService = inject(SsoService);

  public isConnectedEff = effect(() => {
    if (this.ssoService.isConnected() || this.ssoService.hasValidAccessToken()) {
      if (
        this.router.url.startsWith('/callback') ||
        this.router.url.startsWith('/public') ||
        this.router.url === '/'
      ) {
        const savedRedirect = localStorage.getItem('post_login_redirect_uri');
        const isPrivateInitialUrl = this.initialUrl.startsWith('/private') || this.initialUrl.startsWith('/myhome');

        const redirect = savedRedirect
          || (isPrivateInitialUrl ? this.initialUrl : null)
          || '/private/ic/courriers';

        localStorage.removeItem('post_login_redirect_uri');
        this.router.navigateByUrl(redirect);
      }
    }
  });


  async ngOnInit() {
    this.loading = true;
    const loader = this.el.nativeElement.parentElement.querySelector('.loaderPage');
    this.renderer.setStyle(loader, 'display', 'none');

    const lang = localStorage.getItem('lang');
    const allLanguages: string[] = ['fr', 'en', 'nl', 'de'];

    if (!lang || lang === 'undefined' || lang === 'null') {
      const userLang = navigator.language.split('-').reverse()[0].toLowerCase();
      localStorage.setItem('lang', allLanguages.includes(userLang) ? userLang : 'fr');
    }

    await this.translateService.init(localStorage.getItem('lang'), allLanguages, ['assets/i18n', 'assets/translations']);

    if (!this.router.url.startsWith('/callback')) {
      this.reloadApp();
    } else {
      this.loading = false;
    }

    this.translateService.onTranslationReady.subscribe({
      next: (value) => {
        this.initPrimeNGTranslations();
        if (value && !this.router.url.startsWith('/callback')) {
          this.reloadApp();
        }
      },
    });

    const spellcheckerWorker = new Worker(new URL('./spellchecker/spellchecker.worker', import.meta.url), {
      type: 'module',
    });
    this.spellcheckerService.setWorker(spellcheckerWorker);
  }

  private reloadApp(): void {
    this.loading = true;
    setTimeout(() => {
      this.loading = false;
    }, 0);
  }

  private initPrimeNGTranslations(): void {
    this.primeNGConfig.setTranslation({
      startsWith: this.translateService.translate('PRIMENG.STARTSWITH'),
      contains: this.translateService.translate('PRIMENG.CONTAINS'),
      notContains: this.translateService.translate('PRIMENG.NOTCONTAINS'),
      endsWith: this.translateService.translate('PRIMENG.ENDSWITH'),
      equals: this.translateService.translate('PRIMENG.EQUALS'),
      notEquals: this.translateService.translate('PRIMENG.NOTEQUALS'),
      noFilter: this.translateService.translate('PRIMENG.NOFILTER'),
      lt: this.translateService.translate('PRIMENG.LT'),
      lte: this.translateService.translate('PRIMENG.LTE'),
      gt: this.translateService.translate('PRIMENG.GT'),
      gte: this.translateService.translate('PRIMENG.GTE'),
      is: this.translateService.translate('PRIMENG.IS'),
      isNot: this.translateService.translate('PRIMENG.ISNOT'),
      before: this.translateService.translate('PRIMENG.BEFORE'),
      after: this.translateService.translate('PRIMENG.AFTER'),
      dateIs: this.translateService.translate('PRIMENG.DATEIS'),
      dateIsNot: this.translateService.translate('PRIMENG.DATEISNOT'),
      dateBefore: this.translateService.translate('PRIMENG.DATEBEFORE'),
      dateAfter: this.translateService.translate('PRIMENG.DATEAFTER'),
      clear: this.translateService.translate('PRIMENG.CLEAR'),
      apply: this.translateService.translate('PRIMENG.APPLY'),
      matchAll: this.translateService.translate('PRIMENG.MATCHALL'),
      matchAny: this.translateService.translate('PRIMENG.MATCHANY'),
      addRule: this.translateService.translate('PRIMENG.ADDRULE'),
      removeRule: this.translateService.translate('PRIMENG.REMOVERULE'),
      accept: this.translateService.translate('PRIMENG.ACCEPT'),
      reject: this.translateService.translate('PRIMENG.REJECT'),
      choose: this.translateService.translate('PRIMENG.CHOOSE'),
      upload: this.translateService.translate('PRIMENG.UPLOAD'),
      cancel: this.translateService.translate('PRIMENG.CANCEL'),
      dayNames: [
        this.translateService.translate('PRIMENG.DAYNAMES.SUNDAY'),
        this.translateService.translate('PRIMENG.DAYNAMES.MONDAY'),
        this.translateService.translate('PRIMENG.DAYNAMES.TUESDAY'),
        this.translateService.translate('PRIMENG.DAYNAMES.WEDNESDAY'),
        this.translateService.translate('PRIMENG.DAYNAMES.THURSDAY'),
        this.translateService.translate('PRIMENG.DAYNAMES.FRIDAY'),
        this.translateService.translate('PRIMENG.DAYNAMES.SATURDAY'),
      ],
      dayNamesShort: [
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.SUNDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.MONDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.TUESDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.WEDNESDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.THURSDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.FRIDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESSHORT.SATURDAY'),
      ],
      dayNamesMin: [
        this.translateService.translate('PRIMENG.DAYNAMESMIN.SUNDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESMIN.MONDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESMIN.TUESDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESMIN.WEDNESDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESMIN.THURSDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESMIN.FRIDAY'),
        this.translateService.translate('PRIMENG.DAYNAMESMIN.SATURDAY'),
      ],
      monthNames: [
        this.translateService.translate('PRIMENG.MONTHNAMES.JANUARY'),
        this.translateService.translate('PRIMENG.MONTHNAMES.FEBRUARY'),
        this.translateService.translate('PRIMENG.MONTHNAMES.MARCH'),
        this.translateService.translate('PRIMENG.MONTHNAMES.APRIL'),
        this.translateService.translate('PRIMENG.MONTHNAMES.MAY'),
        this.translateService.translate('PRIMENG.MONTHNAMES.JUNE'),
        this.translateService.translate('PRIMENG.MONTHNAMES.JULY'),
        this.translateService.translate('PRIMENG.MONTHNAMES.AUGUST'),
        this.translateService.translate('PRIMENG.MONTHNAMES.SEPTEMBER'),
        this.translateService.translate('PRIMENG.MONTHNAMES.OCTOBER'),
        this.translateService.translate('PRIMENG.MONTHNAMES.NOVEMBER'),
        this.translateService.translate('PRIMENG.MONTHNAMES.DECEMBER'),
      ],
      monthNamesShort: [
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.JANUARY'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.FEBRUARY'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.MARCH'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.APRIL'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.MAY'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.JUNE'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.JULY'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.AUGUST'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.SEPTEMBER'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.OCTOBER'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.NOVEMBER'),
        this.translateService.translate('PRIMENG.MONTHNAMESSHORT.DECEMBER'),
      ],
      dateFormat: this.translateService.translate('PRIMENG.DATEFORMAT'),
      firstDayOfWeek: 1,
      today: this.translateService.translate('PRIMENG.TODAY'),
      weekHeader: this.translateService.translate('PRIMENG.WEEKHEADER'),
      weak: this.translateService.translate('PRIMENG.WEAK'),
      medium: this.translateService.translate('PRIMENG.MEDIUM'),
      strong: this.translateService.translate('PRIMENG.STRONG'),
      passwordPrompt: this.translateService.translate('PRIMENG.PASSWORDPROMPT'),
      emptyMessage: this.translateService.translate('PRIMENG.EMPTYMESSAGE'),
      emptyFilterMessage: this.translateService.translate('PRIMENG.EMPTYFILTERMESSAGE'),
    });
  }
  public ngOnDestroy(): void {
    this.isConnectedEff.destroy();
  }
}